
// src/pages/Dashboard.js
import React, { useEffect } from 'react';
import { Container, Row, Col, Card, Spinner, Alert } from 'react-bootstrap';
import { useQuery } from '@tanstack/react-query';
import StatusCard from '../components/Dashboard/StatusCard';
import useWebsiteStore from '../store/websiteStore';
import { userService } from '../api/services/user';
import {NavLink} from "react-router-dom";
import Websites from "./Websites";

const Dashboard = () => {
    const { websites, fetchWebsites, loading, error } = useWebsiteStore();


    const { data: credits, isLoading: creditsLoading } = useQuery({
        queryKey: ['credits'],
        queryFn: () => userService.getCredits().then(res => res.data)
    });

    const { data: subscription, isLoading: subscriptionLoading } = useQuery({
        queryKey: ['subscription'],
        queryFn: () => userService.getSubscription().then(res => res.data)
    });

    useEffect(() => {
        fetchWebsites();
    }, []);

    // Statistiky
    const statusCounts = websites.reduce((acc, website) => {
        acc[website.status] = (acc[website.status] || 0) + 1;
        return acc;
    }, {});

    if (loading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" role="status" variant="primary">
                    <span className="visually-hidden">Načítání...</span>
                </Spinner>
            </div>
        );
    }

    if (error) {
        return <Alert variant="danger">{error}</Alert>;
    }

    return (
        <div>
        <Container fluid>
            <h1 className="mb-4 ">Přehled</h1>

            <Row className="mb-4">
                <Col lg={3} sm={6} className="mb-4">
                    <NavLink to={"/websites"} className={"text-decoration-none"}>
                        <StatusCard
                            title="Aktivní weby"
                            value={websites.length}
                            icon="fa-globe"
                            status="primary"
                        />
                    </NavLink>
                </Col>
                <Col lg={3} sm={6} className="mb-4">
                    <NavLink to={"/websites"} className={"text-decoration-none"}>
                    <StatusCard
                        title="Online"
                        value={statusCounts.online || 0}
                        icon="fa-check-circle"
                        status="success"
                    />
                    </NavLink>
                </Col>
                <Col lg={3} sm={6} className="mb-4">
                    <NavLink to={"/websites"} className={"text-decoration-none"}>
                    <StatusCard
                        title="Problémy"
                        value={statusCounts.issues || 0}
                        icon="fa-exclamation-triangle"
                        status="warning"
                    />
                    </NavLink>
                </Col>
                <Col lg={3} sm={6} className="mb-4">
                    <NavLink to={"/websites"} className={"text-decoration-none"}>
                    <StatusCard
                        title="Offline"
                        value={statusCounts.offline || 0}
                        icon="fa-times-circle"
                        status="danger"
                    />
                    </NavLink>
                </Col>
            </Row>

            <Row>
                <Col lg={6} className="mb-4">
                    <Card className="h-100 shadow">
                        <Card.Header>
                            <h5 className="mb-0">Kredity a předplatné</h5>
                        </Card.Header>
                        <Card.Body>
                            {creditsLoading ? (
                                <div className="text-center py-3">
                                    <Spinner animation="border" size="sm" />
                                </div>
                            ) : credits ? (
                                <div className="d-flex align-items-center mb-3">
                                    <div className="me-3">
                                        <i className="fas fa-coins fa-2x text-primary"></i>
                                    </div>
                                    <div>
                                        <p className="mb-0">Dostupné kredity</p>
                                        <h3 className="mb-0">{credits.amount}</h3>
                                    </div>
                                </div>
                            ) : (
                                <p>Informace o kreditech nejsou dostupné</p>
                            )}

                            {subscriptionLoading ? (
                                <div className="text-center py-3">
                                    <Spinner animation="border" size="sm" />
                                </div>
                            ) : subscription ? (
                                <div className="mt-4">
                                    <div className="d-flex align-items-center">
                                        <div className="me-3">
                                            <i className="fas fa-crown fa-2x text-warning"></i>
                                        </div>
                                        <div>
                                            <p className="mb-0">Aktuální plán</p>
                                            <h4 className="mb-0">{subscription.plan}</h4>
                                        </div>
                                    </div>
                                    <div className="mt-2 text-muted">
                                        <small>Platnost do: {new Date(subscription.validUntil).toLocaleDateString()}</small>
                                    </div>
                                </div>
                            ) : (
                                <div className="mt-4 p-3 bg-light rounded">
                                    <p className="mb-0 text-center">Žádné aktivní předplatné</p>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>

                <Col lg={6} className="mb-4">
                    <Card className="h-100 shadow">
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">Poslední upozornění</h5>
                            <span className="badge bg-primary rounded-pill">3</span>
                        </Card.Header>
                        <Card.Body>
                            <div className="alerts-list">
                                <div className="alert-item d-flex align-items-start mb-3 p-3 border-start border-4 border-danger rounded bg-light">
                                    <div className="alert-icon me-3 text-danger">
                                        <i className="fas fa-exclamation-circle fa-lg"></i>
                                    </div>
                                    <div className="alert-content">
                                        <div className="d-flex justify-content-between mb-1">
                                            <h6 className="mb-0">example.com</h6>
                                            <small className="text-muted">před 32 minutami</small>
                                        </div>
                                        <p className="mb-0">Web je nedostupný: Error 503 Service Unavailable</p>
                                    </div>
                                </div>

                                <div className="alert-item d-flex align-items-start mb-3 p-3 border-start border-4 border-warning rounded bg-light">
                                    <div className="alert-icon me-3 text-warning">
                                        <i className="fas fa-shield-alt fa-lg"></i>
                                    </div>
                                    <div className="alert-content">
                                        <div className="d-flex justify-content-between mb-1">
                                            <h6 className="mb-0">blog.example.org</h6>
                                            <small className="text-muted">před 3 hodinami</small>
                                        </div>
                                        <p className="mb-0">Nalezeno 2 zastaralé pluginy s bezpečnostními riziky</p>
                                    </div>
                                </div>

                                <div className="alert-item d-flex align-items-start p-3 border-start border-4 border-info rounded bg-light">
                                    <div className="alert-icon me-3 text-info">
                                        <i className="fas fa-sync-alt fa-lg"></i>
                                    </div>
                                    <div className="alert-content">
                                        <div className="d-flex justify-content-between mb-1">
                                            <h6 className="mb-0">myshop.cz</h6>
                                            <small className="text-muted">před 5 hodinami</small>
                                        </div>
                                        <p className="mb-0">Dostupná nová verze WordPress: 6.3.2</p>
                                    </div>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
        </div>
    );
};

export default Dashboard;